import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type PatientDrawerResponse } from '../api/generated';
import { fetchPatientDetails } from '../services/patientDetailsService';
import { useAppDispatch } from '../store/hooks';
import { showError } from '../store/errorDialog/errorDialogSlice';

interface UsePatientDetailsResult {
  data: PatientDrawerResponse | null;
  isLoading: boolean;
  // True only while a fetch is in flight for data already on screen — i.e. a
  // refresh of the patient being displayed. Lets a consumer replace just the
  // stale part of the view instead of blanking everything it has.
  isRefreshing: boolean;
  error: boolean;
}

export function usePatientDetails(
  locationId: string | null,
  patientId: string | null,
  refreshToken: number
): UsePatientDetailsResult {
  const [data, setData] = useState<PatientDrawerResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  // Drop the previous patient's details the moment a different one is asked
  // for, so consumers never render one patient's data under another's id.
  // Done during render rather than in the effect below: an effect would let
  // one frame of the stale patient paint first — the same previous-state
  // idiom the drawer uses for `prevIsOpen`.
  // A refreshToken bump is deliberately excluded: that refetches the *same*
  // patient, and keeping the data is what lets `isRefreshing` below tell a
  // refresh apart from a first load.
  const requestKey = `${locationId ?? ''}|${patientId ?? ''}`;
  const [prevRequestKey, setPrevRequestKey] = useState(requestKey);
  if (requestKey !== prevRequestKey) {
    setPrevRequestKey(requestKey);
    setData(null);
    setError(false);
    setIsLoading(Boolean(locationId && patientId));
  }

  useEffect(() => {
    if (!locationId || !patientId) return;
    let cancelled = false;
    setIsLoading(true);
    setError(false);
    void fetchPatientDetails(locationId, patientId)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setIsLoading(false);
          dispatch(showError({ message: t('fetch-error') }));
        }
      });
    return () => { cancelled = true; };
  }, [locationId, patientId, refreshToken, dispatch, t]);

  // Derived rather than stored: only the refreshToken path can have a fetch in
  // flight over non-null data, because a location/patient change nulls it above.
  return { data, isLoading, isRefreshing: isLoading && data !== null, error };
}
